import { useIsFocused } from '@react-navigation/native';
import { FlashList } from '@shopify/flash-list';
import dayjs from 'dayjs';
import * as Clipboard from 'expo-clipboard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { DateRangePickerWebV3 } from '@components/DateRangePicker/DateRangePickerWebV3';
import Highlighter from '@components/HighlightWords';
import { ContactType } from '@components/Invite/Contact';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { TaskPriorityBadge } from '@components/TaskCard/TaskPriorityBadge';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import {
  PopupTaskSortButton,
  SortOptionValue,
} from '@components/Web/PopupTaskSortButton.web';
import {
  GetMeDocument,
  ProjectMember,
  TaskPriority,
  useGeneratePublicCalendarUrlMutation,
} from '@graphql/generated';
import { useDateRangeString } from '@hooks/useDateRangeString';
import useMe from '@hooks/useMe';
import { useCloseOnOutsideClick } from '@hooks/useOutsideClick.web';
import CustomToast from '@screens/Tasks/CustomToast';
import theme from '@themes/theme';

type Props = {
  initialAssignees?: ContactType[];
  selectedAssignees: ContactType[];
  setSelectedAssignees: (x: ContactType[]) => void;
  selectedDueDateRange: DateRange;
  setSelectedDueDateRange: (x: DateRange) => void;
  selectedPriorities: TaskPriority[];
  setSelectedPriorities: (x: TaskPriority[]) => void;
  selectedSort?: SortOptionValue;
  onSelectSort?: (sort: SortOptionValue) => void;
  projectMembers?: ProjectMember[];
  assigneesLoading?: boolean;
};

type SectionType = 'assignee' | 'dueDate' | 'priority';

export type DueDateOptionType =
  | 'today'
  | 'tomorrow'
  | 'next3days'
  | 'selectDates'
  | null;

/**
 * Whether the "Select Dates" row should show its highlighted (green) state and
 * the picked date-range label. Only true when the user explicitly chose the
 * date picker — NOT when Today/Tomorrow/Next 3 days set a range (TA-9958 #6).
 */
export const isCustomDateRangeActive = (
  option: DueDateOptionType,
  range: { start?: unknown; end?: unknown }
): boolean => option === 'selectDates' && !!(range.start || range.end);

/**
 * Whether the date picker should be seeded with the current range. Only true
 * when the active option is "selectDates" (i.e. the user is reopening a range
 * they picked before). When a quick option (Today/Tomorrow/Next 3 days) is
 * active, the picker opens empty so that option's range does not carry over —
 * the quick option stays selected until the user confirms a date (TA-9958 #6).
 */
export const shouldSeedDatePickerFromRange = (
  option: DueDateOptionType
): boolean => option === 'selectDates';

const ESTIMATED_ITEM_SIZE = 40;

export const ProjectTasksFilterPanel: React.FC<Props> = ({
  initialAssignees = [],
  selectedAssignees,
  setSelectedAssignees,
  selectedDueDateRange,
  setSelectedDueDateRange,
  selectedPriorities,
  setSelectedPriorities,
  selectedSort,
  onSelectSort,
  projectMembers: _projectMembers = [],
  assigneesLoading = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [sortVisible, setSortVisible] = useState(false);
  const [_filterMouseOver, setFilterMouseOver] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    Record<SectionType, boolean>
  >({
    assignee: true,
    dueDate: false,
    priority: false,
  });
  const [search, setSearch] = useState('');
  const [hoveredAssigneeId, setHoveredAssigneeId] = useState('');
  const [hoveredPriorityId, setHoveredPriorityId] = useState('');
  const [hoveredDueDateValue, setHoveredDueDateValue] =
    useState<DueDateOptionType>(null);
  const [selectedDueDateOption, setSelectedDueDateOption] =
    useState<DueDateOptionType>(null);
  const [currentPanel, setCurrentPanel] = useState<'main' | 'dates'>('main');
  const selectedDateRangeStr = useDateRangeString({ selectedDueDateRange });

  const [calendarMouseOver, setCalendarMouseOver] = useState(false);

  const filterBackgroundColor = () => {
    return visible || _filterMouseOver ? 'grey01' : 'white';
  };

  const modalRef = useRef<HTMLDivElement>(null);

  const { me, refetchMe } = useMe();
  const toast = useToast();

  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [calendarModalPosition, setCalendarModalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const [generateCalendarUrl] = useGeneratePublicCalendarUrlMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  const calendarButtonRef = useCloseOnOutsideClick<HTMLDivElement>(
    isCalendarModalOpen,
    () => setIsCalendarModalOpen(false)
  );

  const handleCalendarPress = () => {
    if (!me?.calendarUrl) {
      generateCalendarUrl().then(() => refetchMe());
    }
    setIsCalendarModalOpen(true);
    if (calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setCalendarModalPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  const showCalendarToast = (message: string) => {
    toast.show(<CustomToast message={message} />, {
      type: 'custom',
      placement: 'bottom',
      duration: 2000,
      animationType: 'zoom-in',
    });
  };

  const totalFilterCount = useMemo(() => {
    let count = 0;
    count += selectedAssignees.length;
    count += selectedPriorities.length;
    if (selectedDueDateRange.start || selectedDueDateRange.end) count += 1;
    return count;
  }, [selectedAssignees, selectedPriorities, selectedDueDateRange]);

  const assigneeCount = selectedAssignees.length;
  const priorityCount = selectedPriorities.length;
  const dueDateCount =
    selectedDueDateRange.start || selectedDueDateRange.end ? 1 : 0;

  const buttonRef = useCloseOnOutsideClick<HTMLDivElement>(
    visible,
    () => {
      setVisible(false);
      setCurrentPanel('main');
    },
    { extraRefs: [modalRef] }
  );

  // This panel stays mounted when the user switches to another project tab
  // (Checklist, Files & Media, etc. — the top-tab navigator doesn't unmount
  // inactive tabs), and each popover is portaled to document.body, so an open
  // Filter/Sort/Add-to-calendar dropdown would otherwise keep floating over
  // the newly active tab. Close all three as soon as this tab loses focus
  // (TA-10949 #30).
  const isFocused = useIsFocused();
  useEffect(() => {
    if (!isFocused) {
      setVisible(false);
      setSortVisible(false);
      setIsCalendarModalOpen(false);
      setCurrentPanel('main');
    }
  }, [isFocused]);

  const toggleSection = (section: SectionType) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[section];
      return {
        assignee: false,
        dueDate: false,
        priority: false,
        [section]: !isCurrentlyExpanded,
      };
    });
  };

  const clearAllFilters = () => {
    setSelectedAssignees([]);
    setSelectedPriorities([]);
    setSelectedDueDateRange({ start: undefined, end: undefined });
    setSelectedDueDateOption(null);
    setCurrentPanel('main');
  };

  const handleDueDateOptionSelect = (option: DueDateOptionType) => {
    if (option === 'selectDates') {
      // Opening the picker keeps the current quick-option selection (e.g.
      // Today) highlighted; it is only replaced once the user confirms a
      // specific date in the picker. The picker opens empty unless a custom
      // range was previously chosen — see its `start`/`end` props (TA-9958 #6).
      setCurrentPanel('dates');
      return;
    }

    setSelectedDueDateOption(option);

    const today = dayjs().startOf('day');

    switch (option) {
      case 'today':
        setSelectedDueDateRange({
          start: today,
          end: today,
        });
        break;
      case 'tomorrow':
        const tomorrow = today.add(1, 'day');
        setSelectedDueDateRange({
          start: tomorrow,
          end: tomorrow,
        });
        break;
      case 'next3days':
        setSelectedDueDateRange({
          start: today,
          end: today.add(3, 'day'),
        });
        break;
      default:
        setSelectedDueDateRange({ start: undefined, end: undefined });
    }
  };

  const onPressAssignee = (contact: ContactType) => {
    const isIn = selectedAssignees.some((c) => c.id === contact.id);
    const updatedSelected = isIn
      ? selectedAssignees.filter((c) => c.id !== contact.id)
      : [...selectedAssignees, contact];
    setSelectedAssignees(updatedSelected);
  };

  const priorityList: TaskPriority[] = [
    TaskPriority.High,
    TaskPriority.Medium,
    TaskPriority.Low,
  ];

  const onPressPriority = (item: TaskPriority) => {
    const isIn = selectedPriorities.some((t) => t === item);
    const updatedSelected = isIn
      ? selectedPriorities.filter((c) => c !== item)
      : [...selectedPriorities, item];
    const sorted = priorityList.filter((r) =>
      updatedSelected.some((t) => t === r)
    );
    setSelectedPriorities(sorted);
  };

  const filteredAssignees = useMemo(() => {
    return [...initialAssignees]
      .filter(
        (u) =>
          search.length === 0 ||
          u.displayName?.toLowerCase().includes(search.toLowerCase().trim())
      )
      .sort((a, b) => (a.firstName ?? '')?.localeCompare(b.firstName ?? ''));
  }, [initialAssignees, search]);

  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const adjustedBounds =
    buttonRef.current &&
    layout &&
    (() => {
      const client = buttonRef.current.getBoundingClientRect();
      return {
        top: client.top + client.height + 6,
        left: client.left,
      };
    })();

  const renderBadge = (count: number) => {
    if (count === 0) return null;
    return (
      <Box
        backgroundColor='greenSecondary'
        borderRadius='xl'
        width={20}
        height={20}
        alignItems='center'
        justifyContent='center'
        marginLeft='xs'>
        <Text variant='metadata' color='white'>
          {count}
        </Text>
      </Box>
    );
  };

  const renderSectionHeader = (
    title: string,
    section: SectionType,
    count: number
  ) => {
    const isExpanded = expandedSections[section];
    return (
      <TouchableOpacity onPress={() => toggleSection(section)}>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          paddingVertical='m'
          paddingHorizontal='m'
          borderBottomWidth={1}
          borderBottomColor='grey01'>
          <Box flexDirection='row' alignItems='center'>
            <Text variant='labelEmphasized' color='textPrimary'>
              {title}
            </Text>
            {renderBadge(count)}
          </Box>
          <Icon
            height={24}
            width={24}
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            color='textPrimary'
          />
        </Box>
      </TouchableOpacity>
    );
  };

  const renderAssigneeSection = () => {
    if (!expandedSections.assignee) return null;
    if (assigneesLoading) {
      return (
        <Box paddingVertical='s' alignItems='center'>
          <ActivityIndicatorLoading />
        </Box>
      );
    }
    if (filteredAssignees.length === 0) {
      return (
        <Box paddingHorizontal='m' paddingVertical='s'>
          <Text variant='webBodyHintSubline' color='grey04'>
            No Assignee Found
          </Text>
        </Box>
      );
    }
    return (
      <Box style={{ maxHeight: 200 }} paddingBottom='s'>
        <FlashList
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          data={filteredAssignees}
          extraData={`${hoveredAssigneeId}|${selectedAssignees
            .map((a) => a.id)
            .join(',')}`}
          renderItem={({ item: contact }) => {
            const { id, avatar, displayName, name } = contact;
            return (
              <Box
                accessibilityLabel='Assignee Item'
                style={{
                  paddingVertical: 9,
                  backgroundColor:
                    hoveredAssigneeId !== '' && hoveredAssigneeId === contact.id
                      ? theme.colors.grey01
                      : theme.colors.white,
                }}
                onMouseEnter={() => setHoveredAssigneeId(id)}
                onMouseLeave={() => setHoveredAssigneeId('')}>
                <TouchableOpacity
                  onPress={() => onPressAssignee(contact)}
                  accessibilityLabel={displayName ? displayName : 'No Name'}>
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='space-between'>
                    <Box ml='m' justifyContent='center' marginRight='xs'>
                      <CheckBoxButton
                        checked={selectedAssignees.some(
                          (s) => s.id === contact.id
                        )}
                      />
                    </Box>
                    <Avatar id={id} label={name} avatar={avatar} size='small' />
                    <Box mr='m' marginLeft='xs' flex={1}>
                      <Text
                        variant='webBodySecondary'
                        color={displayName ? 'textPrimary' : 'grey04'}>
                        {displayName ? (
                          <Highlighter
                            highlightStyle={{
                              backgroundColor: theme.colors.yellowBright,
                            }}
                            autoEscape
                            searchWords={search?.split(' ') || ''}
                            textToHighlight={displayName}
                          />
                        ) : (
                          'No name'
                        )}
                      </Text>
                    </Box>
                  </Box>
                </TouchableOpacity>
              </Box>
            );
          }}
          estimatedItemSize={ESTIMATED_ITEM_SIZE}
        />
      </Box>
    );
  };

  const dueDateOptions: { label: string; value: DueDateOptionType }[] = [
    { label: 'Today', value: 'today' },
    { label: 'Tomorrow', value: 'tomorrow' },
    { label: 'Next 3 days', value: 'next3days' },
  ];

  const renderDueDateOption = (
    label: string,
    value: DueDateOptionType,
    isSelectDates = false
  ) => {
    const isSelected = selectedDueDateOption === value;
    // "Select Dates" should only reflect a range the user picked via the date
    // picker — not a range set by the Today/Tomorrow/Next 3 days quick options
    // (TA-9958 #6). Gate its highlight on the option actually being selectDates.
    const isCustomDatesActive = isCustomDateRangeActive(
      selectedDueDateOption,
      selectedDueDateRange
    );
    const displayLabel =
      isSelectDates && isCustomDatesActive
        ? (selectedDateRangeStr ?? label)
        : label;
    const iconColor =
      isSelectDates && isCustomDatesActive ? 'greenSecondary' : 'textPrimary';
    const textColor =
      isSelectDates && isCustomDatesActive ? 'greenSecondary' : 'textPrimary';

    return (
      <TouchableOpacity
        key={value}
        onPress={() => handleDueDateOptionSelect(value)}
        accessibilityLabel={label}>
        <Box
          flexDirection='row'
          alignItems='center'
          paddingVertical='s'
          paddingHorizontal='m'
          backgroundColor={hoveredDueDateValue === value ? 'grey01' : 'white'}
          onMouseEnter={() => setHoveredDueDateValue(value)}
          onMouseLeave={() => setHoveredDueDateValue(null)}>
          {isSelectDates ? (
            <Icon variant='s' name='Calendar' color={iconColor} />
          ) : (
            <RadioButton
              isSelected={isSelected}
              onPress={() => handleDueDateOptionSelect(value)}
              iconVariant='m'
            />
          )}
          <Text marginLeft='xs' variant='webBodySecondary' color={textColor}>
            {displayLabel}
          </Text>
        </Box>
      </TouchableOpacity>
    );
  };

  const renderDueDateSection = () => {
    if (!expandedSections.dueDate) return null;
    return (
      <Box paddingBottom='s' backgroundColor='white'>
        {dueDateOptions.map((option) =>
          renderDueDateOption(option.label, option.value)
        )}
        {renderDueDateOption('Select Dates', 'selectDates', true)}
      </Box>
    );
  };

  const renderPrioritySection = () => {
    if (!expandedSections.priority) return null;
    return (
      <Box paddingBottom='s'>
        <FlashList
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          data={priorityList}
          extraData={`${hoveredPriorityId}|${selectedPriorities.join(',')}`}
          renderItem={({ item, index }) => {
            return (
              <Box
                style={{
                  paddingVertical: 9,
                  backgroundColor:
                    hoveredPriorityId !== '' &&
                    hoveredPriorityId === index.toString()
                      ? theme.colors.grey01
                      : theme.colors.white,
                }}
                onMouseEnter={() => setHoveredPriorityId(index.toString())}
                onMouseLeave={() => setHoveredPriorityId('')}>
                <TouchableOpacity
                  onPress={() => onPressPriority(item)}
                  accessibilityLabel={item.toString()}>
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='space-between'>
                    <Box ml='m' justifyContent='center' marginRight='xs'>
                      <CheckBoxButton
                        checked={selectedPriorities.some((t) => t === item)}
                      />
                    </Box>
                    <Box mr='m' flex={1}>
                      <TaskPriorityBadge priority={item} />
                    </Box>
                  </Box>
                </TouchableOpacity>
              </Box>
            );
          }}
          estimatedItemSize={ESTIMATED_ITEM_SIZE}
        />
      </Box>
    );
  };

  const renderSepeartion = () => {
    return (
      <Box
        alignItems='center'
        borderTopWidth={1}
        borderTopColor='grey02'
        backgroundColor='white'></Box>
    );
  };

  return (
    <Box flexDirection='row' alignItems='center'>
      <Box ref={buttonRef} flexDirection='row'>
        <TouchableOpacity
          accessibilityLabel='Filter'
          onPress={() => {
            setSortVisible(false);
            setVisible((v) => {
              if (v) setCurrentPanel('main');
              return !v;
            });
          }}>
          <Box
            flexDirection='row'
            alignItems='center'
            height={36}
            paddingHorizontal='m'
            borderRadius='xs'
            backgroundColor={filterBackgroundColor()}
            onMouseEnter={() => setFilterMouseOver(true)}
            onMouseLeave={() => setFilterMouseOver(false)}>
            <Icon variant='s' name='AlignCenterDesc' color='textSecondary' />
            <Text marginLeft='xs' variant='webBody' color='textPrimary'>
              Filter
            </Text>
            {totalFilterCount > 0 && (
              <>
                <Box
                  backgroundColor='greenSecondary'
                  width={20}
                  height={20}
                  alignItems='center'
                  justifyContent='center'
                  marginLeft='xs'
                  style={{ borderRadius: 10 }}>
                  <Text variant='metadata' color='white'>
                    {totalFilterCount}
                  </Text>
                </Box>
                <TouchableOpacity
                  onPress={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                  style={{ marginLeft: 8 }}>
                  <Icon variant='s' name='X' color='textPrimary' />
                </TouchableOpacity>
              </>
            )}
          </Box>
        </TouchableOpacity>

        {!layout && (
          <Box
            left='-200vw'
            position='absolute'
            visible={!layout}
            onLayout={({ nativeEvent: { layout: l } }) => {
              setLayout((curr) => curr ?? l);
            }}>
            <ShadowBox
              width={380}
              borderRadius='xs'
              variant='base'
              backgroundColor='white'>
              <Box height={500} />
            </ShadowBox>
          </Box>
        )}

        {visible &&
          createPortal(
            <Box
              ref={modalRef}
              position='absolute'
              style={{ ...(adjustedBounds ?? {}), zIndex: 9999 }}
              width={380}>
              {currentPanel === 'main' ? (
                <ShadowBox
                  borderRadius='xs'
                  variant='base'
                  backgroundColor='white'
                  style={{ maxHeight: 600 }}>
                  <Box paddingHorizontal='m' paddingTop='m' paddingBottom='m'>
                    <SearchInput
                      value={search}
                      height={40}
                      onTextChanged={(value: string) => setSearch(value)}
                      placeHolder='Search'
                    />
                  </Box>

                  {renderSepeartion()}
                  <Box borderTopWidth={1} borderTopColor='grey01'>
                    {renderSectionHeader('Assignee', 'assignee', assigneeCount)}
                  </Box>
                  {renderAssigneeSection()}
                  {renderSepeartion()}

                  {renderSectionHeader('Due Date', 'dueDate', dueDateCount)}
                  {renderDueDateSection()}
                  {renderSepeartion()}

                  {renderSectionHeader('Priority', 'priority', priorityCount)}
                  {renderPrioritySection()}
                  {renderSepeartion()}

                  <TouchableOpacity onPress={clearAllFilters}>
                    <Box
                      paddingVertical='m'
                      alignItems='center'
                      borderTopWidth={1}
                      borderTopColor='grey01'
                      backgroundColor='white'>
                      <Text variant='webBodySecondary' color='textPrimary'>
                        Clear All
                      </Text>
                    </Box>
                  </TouchableOpacity>
                </ShadowBox>
              ) : (
                <ShadowBox
                  flex={1}
                  width={352}
                  alignItems='center'
                  justifyContent='center'
                  borderRadius='xs'
                  variant='base'
                  backgroundColor='white'>
                  <Box flex={1} style={{ width: 352 }}>
                    <DateRangePickerWebV3
                      isRangeSelector={false}
                      onSelect={(d1, d2) => {
                        setSelectedDueDateRange({
                          start: d1 ? dayjs(d1) : undefined,
                          end: d2 ? dayjs(d2) : undefined,
                        });
                        setSelectedDueDateOption('selectDates');
                        setCurrentPanel('main');
                      }}
                      start={
                        shouldSeedDatePickerFromRange(selectedDueDateOption)
                          ? selectedDueDateRange.start?.toDate()
                          : undefined
                      }
                      end={
                        shouldSeedDatePickerFromRange(selectedDueDateOption)
                          ? selectedDueDateRange.end?.toDate()
                          : undefined
                      }
                    />
                  </Box>
                </ShadowBox>
              )}
            </Box>,
            document.body
          )}
      </Box>
      {onSelectSort && (
        <PopupTaskSortButton
          title='Sort'
          selectedSort={selectedSort}
          onSelectSort={onSelectSort}
          boxProps={{ position: ['right', 'bottom'], offset: 4 }}
          isOpen={sortVisible}
          onToggle={(open) => {
            if (open) setVisible(false);
            setSortVisible(open);
          }}
        />
      )}
      <Box ref={calendarButtonRef}>
        <TouchableOpacity
          accessibilityLabel='Add to calendar'
          onPress={handleCalendarPress}>
          <Box
            flexDirection='row'
            alignItems='center'
            paddingVertical='xs'
            paddingHorizontal='m'
            borderRadius='s'
            backgroundColor={calendarMouseOver ? 'grey01' : 'white'}
            onMouseEnter={() => setCalendarMouseOver(true)}
            onMouseLeave={() => setCalendarMouseOver(false)}>
            <Icon variant='s' name='Calendar' color='textSecondary' />
            <Text marginLeft='xs' variant='webBody' color='black'>
              Add to calendar
            </Text>
          </Box>
        </TouchableOpacity>
      </Box>
      {isCalendarModalOpen &&
        createPortal(
          <Box
            position='absolute'
            style={{
              top: calendarModalPosition.top,
              left: calendarModalPosition.left,
              width: calendarModalPosition.width,
            }}>
            <Box flexShrink={1}>
              <ShadowBox
                flex={1}
                flexDirection='row'
                borderRadius='xs'
                backgroundColor='white'
                justifyContent='flex-start'>
                <Button
                  fullWidth
                  onPress={() => {
                    Clipboard.setStringAsync(me?.calendarUrl ?? '')
                      .then(() => {
                        showCalendarToast('Calendar Link copied to clipboard');
                      })
                      .then(() => {
                        setIsCalendarModalOpen(false);
                      });
                  }}
                  variant='text'
                  prefix={<Icon variant='s' name='Link' color='black' />}>
                  <Text variant='webSmall'>Copy URL</Text>
                </Button>
              </ShadowBox>
            </Box>
          </Box>,
          document.body
        )}
    </Box>
  );
};
